import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import type { Task, Trip } from "@/api.types.v2";

// New interface for task filtering and pagination
export interface TasksQueryParams {
  page?: number;
  limit?: number | "all"; // Allow "all" as a valid value
  status?: string;
  type?: string;
  tripId?: string | null; // Use null instead of "null" string
  operatorId?: string;
  shipmentId?: string; // New field for shipment-based tasks
  orderId?: string; // Deprecated - use shipmentId
  orderNumber?: string;
  search?: string;
  /**
   * Comma-separated carrier ids (e.g. `"a,b,c"`). Empty string is forwarded
   * verbatim and the server short-circuits to an empty payload (ezx-server
   * PR #277). `undefined` = don't send the param = no narrowing.
   */
  carrierIds?: string;
  /** Same shape as `carrierIds`. */
  dispatchCenterIds?: string;
}

export const taskSearchQueries = {
  activePickupTasks: {
    limit: "all" as const,
    status: "PENDING,IN_PROGRESS",
    type: "CUSTOMER_PICKUP",
    tripId: "null",
  },
  activeDeliveryTasks: {
    limit: "all" as const,
    status: "PENDING,IN_PROGRESS",
    type: "CUSTOMER_DELIVERY",
    tripId: "null",
  },
  activeCarrierDropoffTasks: {
    limit: "all" as const,
    status: "PENDING,IN_PROGRESS",
    type: "CARRIER_DROPOFF",
    tripId: "null",
  },
  activeReturnTasks: {
    limit: "all" as const,
    status: "PENDING,IN_PROGRESS",
    type: "CUSTOMER_RETURN",
    tripId: "null",
  },
  activeCarrierPickupTasks: {
    limit: "all" as const,
    status: "PENDING,IN_PROGRESS",
    type: "CARRIER_PICKUP",
    tripId: "null",
  },
  // This query fetches ALL active tasks (both assigned and unassigned)
  // The frontend will filter by tripId after fetching
  activeTasks: {
    limit: "all" as const,
    status: "PENDING,IN_PROGRESS",
    type: "CUSTOMER_PICKUP,CUSTOMER_DELIVERY,CUSTOMER_RETURN,CARRIER_DROPOFF,CARRIER_PICKUP",
    // No tripId filter - get all tasks regardless of trip assignment
  },
};

export const taskQueries = {
  activePickupTasks: () =>
    taskQueries.tasksSearchQueryBuilder(taskSearchQueries.activePickupTasks),
  activeDeliveryTasks: () =>
    taskQueries.tasksSearchQueryBuilder(taskSearchQueries.activeDeliveryTasks),
  activeCarrierDropoffTasks: () =>
    taskQueries.tasksSearchQueryBuilder(
      taskSearchQueries.activeCarrierDropoffTasks
    ),
  activeReturnTasks: () =>
    taskQueries.tasksSearchQueryBuilder(taskSearchQueries.activeReturnTasks),
  activeCarrierPickupTasks: () =>
    taskQueries.tasksSearchQueryBuilder(
      taskSearchQueries.activeCarrierPickupTasks
    ),
  activeTasks: () =>
    taskQueries.tasksSearchQueryBuilder(taskSearchQueries.activeTasks),
  taskIdQueryBuilder: (taskId: string) => ["tasks", taskId],
  tasksSearchQueryBuilder: (params: TasksQueryParams) => [
    "tasks",
    {
      ...params,
    },
  ],
};

// Define the input type for creating a task, based on the backend schema
export interface CreateTaskInput {
  type:
    | "CUSTOMER_PICKUP"
    | "CUSTOMER_DELIVERY"
    | "CUSTOMER_RETURN"
    | "CARRIER_DROPOFF"
    | "DEPOT_RECEIVE"
    | "DEPOT_SORT"
    | "DEPOT_DISPATCH"
    | "DEPOT_TRANSFER";
  status?: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED" | "CANCELLED";
  shipmentId: string; // Changed from orderId/parcelId
  dispatchCenterId: string;
  description?: string;
  tripId?: string;
  operatorId?: string;
  addressId?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    province: string;
    country: string;
    postalCode?: string;
    location: {
      lat: number;
      lng: number;
    };
    plusCode?: string;
  };
}

export const useUpdateTaskStatus = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: Error) => void;
}) => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: { taskId: string; status: string }) => {
      const { data } = await axios.patch<Task>(
        `${import.meta.env.VITE_API_URL}/api/v2/tasks/${values.taskId}/status`,
        { status: values.status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: taskQueries.taskIdQueryBuilder(data.id),
        }),
        // Invalidate trip queries to refresh waypoint/task state
        queryClient.invalidateQueries({ queryKey: ["trips"] }),
        queryClient.invalidateQueries({ queryKey: ["waypoints"] }),
        queryClient.invalidateQueries({ queryKey: ["all-waypoints"] }),
      ]);
      onSuccess();
    },
    onError: (error) => {
      onError(error);
    },
  });
};

interface ReplaceTripTasksResponse {
  message: string;
  taskIds: string[];
  assignedTaskIds: string[];
  unassignedTaskIds: string[];
  tripIds: string[];
}

async function getTripTaskIds(tripId: string, token?: string | null) {
  const { data } = await axios.get<Trip>(
    `${import.meta.env.VITE_API_URL}/api/v2/trips/${tripId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data.tasks?.map((task) => task.id) ?? [];
}

function getChangedTripTaskIds(data: ReplaceTripTasksResponse) {
  return Array.from(
    new Set([...data.assignedTaskIds, ...data.unassignedTaskIds])
  );
}

// Create assign tasks mutation
export function useAssignTasksToTrip({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: {
      tripId: string;
      taskIds: string[];
      verifiedBuildingVisits?: Array<{
        taskIds: string[];
        reviewedTaskIds: string[];
        waypointId?: string;
        anchorTaskId?: string;
        confirmed: true;
      }>;
    }) => {
      const currentTaskIds = await getTripTaskIds(values.tripId, token);
      const nextTaskIds = Array.from(
        new Set([...currentTaskIds, ...values.taskIds])
      );
      const { data } = await axios.put<ReplaceTripTasksResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/trips/${values.tripId}/tasks`,
        {
          taskIds: nextTaskIds,
          ...(values.verifiedBuildingVisits?.length
            ? { verifiedBuildingVisits: values.verifiedBuildingVisits }
            : {}),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async (data) => {
      const changedTaskIds = getChangedTripTaskIds(data);
      if (changedTaskIds.length > 0) {
        await Promise.all(
          changedTaskIds.map((taskId) =>
            queryClient.invalidateQueries({
              queryKey: taskQueries.taskIdQueryBuilder(taskId),
            })
          )
        );
      }
      if (data.tripIds.length > 0) {
        const tripIds = data.tripIds;
        await Promise.all(
          tripIds.map((tripId) =>
            queryClient.invalidateQueries({ queryKey: ["trips", tripId] })
          )
        );
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: taskQueries.activePickupTasks(),
        }),
        queryClient.invalidateQueries({
          queryKey: taskQueries.activeDeliveryTasks(),
        }),
        queryClient.invalidateQueries({
          queryKey: ["all-waypoints"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["trips", { status: "PLANNED,ACTIVE,COMPLETED" }],
        }),
      ]);
      onSuccess();
    },
    onError: (error) => {
      onError(error);
    },
  });
}

export function useUnassignTasksFromTrip({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: { tripId: string; taskIds: string[] }) => {
      const taskIdsToRemove = new Set(values.taskIds);
      const currentTaskIds = await getTripTaskIds(values.tripId, token);
      const nextTaskIds = currentTaskIds.filter(
        (taskId) => !taskIdsToRemove.has(taskId)
      );
      const { data } = await axios.put<ReplaceTripTasksResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/trips/${values.tripId}/tasks`,
        { taskIds: nextTaskIds },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async (data) => {
      const changedTaskIds = getChangedTripTaskIds(data);
      if (changedTaskIds.length > 0) {
        await Promise.all(
          changedTaskIds.map((taskId) =>
            queryClient.invalidateQueries({
              queryKey: taskQueries.taskIdQueryBuilder(taskId),
            })
          )
        );
      }

      if (data.tripIds.length > 0) {
        const tripIds = data.tripIds;
        await Promise.all(
          tripIds.map((tripId) =>
            queryClient.invalidateQueries({ queryKey: ["trips", tripId] })
          )
        );
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["all-waypoints"] }),
        queryClient.invalidateQueries({
          queryKey: taskQueries.activePickupTasks(),
        }),
        queryClient.invalidateQueries({
          queryKey: taskQueries.activeDeliveryTasks(),
        }),
      ]);
      onSuccess();
    },
    onError: (error) => {
      onError(error);
    },
  });
}

// Updated hook with support for pagination, filtering, and search
export const useTasks = (params: TasksQueryParams = {}) => {
  const { token } = useSession();
  const {
    page = 1,
    limit = 100,
    status,
    type,
    tripId,
    operatorId,
    shipmentId,
    orderId,
    orderNumber,
    search,
    carrierIds,
    dispatchCenterIds,
  } = params;

  return useQuery<{
    data: Task[];
    metadata: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }>({
    queryKey: taskQueries.tasksSearchQueryBuilder({
      page,
      limit,
      status,
      type,
      tripId,
      operatorId,
      shipmentId,
      orderId,
      orderNumber,
      search,
      carrierIds,
      dispatchCenterIds,
    }),
    queryFn: async () => {
      // Build query string from parameters
      const queryParams = new URLSearchParams();
      if (page) queryParams.append("page", page.toString());

      // Handle the special "all" limit case
      if (limit === "all") {
        queryParams.append("limit", "all");
      } else if (limit) {
        queryParams.append("limit", limit.toString());
      }

      if (status) queryParams.append("status", status);
      if (type) queryParams.append("type", type);
      if (tripId) queryParams.append("tripId", tripId);
      if (operatorId) queryParams.append("operatorId", operatorId);
      if (shipmentId) queryParams.append("shipmentId", shipmentId);
      if (orderId) queryParams.append("orderId", orderId);
      if (orderNumber) queryParams.append("orderNumber", orderNumber);
      if (search) queryParams.append("search", search);
      // Forward verbatim — including the empty string. Server short-circuits
      // empty carrierIds= to an empty payload.
      if (carrierIds !== undefined)
        queryParams.append("carrierIds", carrierIds);
      if (dispatchCenterIds !== undefined)
        queryParams.append("dispatchCenterIds", dispatchCenterIds);

      const { data } = await axios.get<{
        data: Task[];
        metadata: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        };
      }>(
        `${import.meta.env.VITE_API_URL}/api/v2/tasks?${queryParams.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
};

// New hook for creating tasks
export function useCreateTask({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: Task) => void;
  onError?: (error: Error) => void;
} = {}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: CreateTaskInput) => {
      const { data } = await axios.post<Task>(
        `${import.meta.env.VITE_API_URL}/api/v2/tasks`,
        values,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: taskQueries.activePickupTasks(),
      });
      await queryClient.invalidateQueries({
        queryKey: taskQueries.activeDeliveryTasks(),
      });

      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      if (onError) onError(error);
    },
  });
}

// Hook for completing a task
export const useCompleteTask = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
} = {}) => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: { taskId: string; notes?: string }) => {
      const { data } = await axios.patch<Task>(
        `${import.meta.env.VITE_API_URL}/api/v2/tasks/${values.taskId}/complete`,
        { notes: values.notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: taskQueries.taskIdQueryBuilder(data.id),
      });
      // Invalidate trip data if task belongs to a trip
      if (data.tripId) {
        await queryClient.invalidateQueries({
          queryKey: ["trips", data.tripId],
        });
      }
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

// Hook for failing a single task
export const useFailTask = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
} = {}) => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: {
      taskId: string;
      reason: string;
      reasonCode?: number;
      notes?: string;
    }) => {
      const { data } = await axios.patch<{ message: string }>(
        `${import.meta.env.VITE_API_URL}/api/v2/tasks/${values.taskId}/fail`,
        {
          reason: values.reason,
          reasonCode: values.reasonCode,
          notes: values.notes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async (_data, variables) => {
      // Invalidate task query
      await queryClient.invalidateQueries({
        queryKey: taskQueries.taskIdQueryBuilder(variables.taskId),
      });
      // Invalidate trip queries (task may belong to a trip)
      await queryClient.invalidateQueries({
        queryKey: ["trips"],
      });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

// Utility function to filter tasks by map bounds and other criteria
export function filterTasksByBounds(
  tasks: Task[],
  bounds: google.maps.LatLngBounds,
  options: {
    showPickups?: boolean;
    showDeliveries?: boolean;
    showCarrierDropoffs?: boolean;
    showReturns?: boolean;
    showTrips?: boolean;
  }
): Task[] {
  const {
    showPickups = false,
    showDeliveries = false,
    showCarrierDropoffs = false,
    showReturns = false,
    showTrips = false,
  } = options;

  return tasks.filter((task) => {
    if (!task.address?.location) return false;

    // Check if task is within bounds
    const taskLatLng = new google.maps.LatLng(
      task.address.location.lat,
      task.address.location.lng
    );
    if (!bounds.contains(taskLatLng)) return false;

    // Apply toggle filters based on task type
    const taskType = task.type as string;
    if (taskType === "CUSTOMER_PICKUP" && !showPickups) return false;
    if (taskType === "CUSTOMER_DELIVERY" && !showDeliveries) return false;
    if (taskType === "CARRIER_DROPOFF" && !showCarrierDropoffs) return false;
    if (taskType === "CUSTOMER_RETURN" && !showReturns) return false;

    // Check if task is part of a trip
    if (!showTrips && task.tripId) return false;

    // Only show pending or in-progress tasks
    const taskStatus = task.status as string;
    if (taskStatus !== "PENDING" && taskStatus !== "IN_PROGRESS") return false;

    // If task has a tripId, it should already be filtered by showTrips
    if (task.tripId) return false;

    return true;
  });
}

// Carrier dropoff grouping types
export interface CarrierTaskGroup {
  carrierCode: string;
  carrierName: string;
  tasks: Task[];
}

// Hook for fetching carrier dropoffs grouped by carrier
export const useCarrierDropoffsGrouped = () => {
  const { token } = useSession();

  return useQuery<{
    data: CarrierTaskGroup[];
    metadata: {
      total: number;
      groupCount: number;
      taskCount: number;
    };
  }>({
    queryKey: ["tasks", "carrier-dropoffs", "grouped"],
    queryFn: async () => {
      const params = new URLSearchParams({
        type: "CARRIER_DROPOFF",
        status: "PENDING,IN_PROGRESS",
        tripId: "null",
        groupBy: "carrier",
        limit: "all",
      });

      const response = await axios.get<{
        data: CarrierTaskGroup[];
        metadata: {
          total: number;
          groupCount: number;
          taskCount: number;
        };
      }>(`${import.meta.env.VITE_API_URL}/api/v2/tasks?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    enabled: !!token,
  });
};

/**
 * Carrier-dropoff redesign phase D: list ONLY unrouted carrier-dropoff
 * tasks (carrierDropoffLocationId IS NULL). Drives the dispatch /Tasks tab
 * "exception inbox" — most days this returns an empty list because order
 * creation auto-assigns from the grant default.
 *
 * Returns the same `CarrierTaskGroup[]` shape as `useCarrierDropoffsGrouped`
 * for UI compatibility — same groupBy=carrier, just narrowed.
 */
export const useUnroutedCarrierDropoffs = () => {
  const { token } = useSession();

  return useQuery<{
    data: CarrierTaskGroup[];
    metadata: {
      total: number;
      groupCount: number;
      taskCount: number;
    };
  }>({
    queryKey: ["tasks", "carrier-dropoffs", "unrouted"],
    queryFn: async () => {
      const params = new URLSearchParams({
        missingCarrierDropoff: "true",
        status: "PENDING,IN_PROGRESS",
        tripId: "null",
        groupBy: "carrier",
        limit: "all",
      });
      const response = await axios.get<{
        data: CarrierTaskGroup[];
        metadata: {
          total: number;
          groupCount: number;
          taskCount: number;
        };
      }>(`${import.meta.env.VITE_API_URL}/api/v2/tasks?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    enabled: !!token,
  });
};

/**
 * Dispatch-center receiving (lean "A"): list drop-off deliveries awaiting
 * receipt at the dispatch center — the no-pickup OWNED `CUSTOMER_DELIVERY`
 * case with `receivedAt == null`. Server applies the awaiting-receipt
 * predicate (`awaitingReceipt=only`); these rows are HIDDEN from the main
 * backlog until a planner marks them received.
 *
 * Same `CarrierTaskGroup[]` shape as `useUnroutedCarrierDropoffs` (groupBy=carrier)
 * for UI compatibility.
 */
export const useAwaitingReceiptTasks = () => {
  const { token } = useSession();

  return useQuery<{
    data: CarrierTaskGroup[];
    metadata: {
      total: number;
      groupCount: number;
      taskCount: number;
    };
  }>({
    queryKey: ["tasks", "awaiting-receipt"],
    queryFn: async () => {
      const params = new URLSearchParams({
        awaitingReceipt: "only",
        groupBy: "carrier",
        limit: "all",
      });
      const response = await axios.get<{
        data: CarrierTaskGroup[];
        metadata: {
          total: number;
          groupCount: number;
          taskCount: number;
        };
      }>(`${import.meta.env.VITE_API_URL}/api/v2/tasks?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    },
    enabled: !!token,
  });
};

/** Per-task row returned by `POST /tasks/bulk-receive` (server isolates
 * per-task failures and still returns 200; failed rows carry an `error`). */
interface BulkReceiveRow {
  id: string;
  changed: boolean;
  estimatedDelivery?: string | null;
  error?: string;
}

/** Outcome of a (single or bulk) mark-received call, surfaced to the caller so
 * the UI can distinguish full success from a partial batch failure. */
export interface MarkReceivedResult {
  requested: number;
  received: number;
  failed: number;
  failures: { id: string; error: string }[];
}

/**
 * Mark customer drop-off deliveries received at the dispatch center. Stamps
 * `Task.receivedAt`, recomputes the delivery estimate (anchored at receipt),
 * and notifies the customer (server side). Idempotent — re-receiving a task
 * is a no-op. A single id hits `POST /tasks/:id/receive`; multiple ids hit the
 * `POST /tasks/bulk-receive` batch endpoint, which returns 200 even when some
 * tasks fail (e.g. `NOT_AWAITING_RECEIPT`). The mutationFn inspects the
 * per-task results and returns a `MarkReceivedResult` so the caller can show
 * "received X, Y failed" instead of an always-green toast.
 * `dispatchCenterId` is optional — the server resolves the serving center when
 * exactly one is active and 409s otherwise.
 */
export const useMarkReceived = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: {
      taskIds: string[];
      dispatchCenterId?: string;
    }): Promise<MarkReceivedResult> => {
      const headers = { Authorization: `Bearer ${token}` };
      if (vars.taskIds.length === 1) {
        // Single endpoint throws on failure (non-2xx) → surfaces via onError.
        await axios.post(
          `${import.meta.env.VITE_API_URL}/api/v2/tasks/${vars.taskIds[0]}/receive`,
          { dispatchCenterId: vars.dispatchCenterId },
          { headers }
        );
        return { requested: 1, received: 1, failed: 0, failures: [] };
      }
      // Bulk endpoint is partial-failure tolerant: per-task `error` codes ride
      // a 200. Count successes vs failures from the body, never the status.
      const { data } = await axios.post<{ data: BulkReceiveRow[] }>(
        `${import.meta.env.VITE_API_URL}/api/v2/tasks/bulk-receive`,
        { taskIds: vars.taskIds, dispatchCenterId: vars.dispatchCenterId },
        { headers }
      );
      const rows = data.data ?? [];
      const failures = rows
        .filter((r) => r.error)
        .map((r) => ({ id: r.id, error: r.error as string }));
      return {
        requested: vars.taskIds.length,
        received: rows.length - failures.length,
        failed: failures.length,
        failures,
      };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
};

/**
 * Bulk-assign a CarrierDropoffLocation to a batch of unrouted CARRIER_DROPOFF
 * tasks. Used by the dispatch exception inbox override path. Server validates
 * that all tasks share one shipment carrier matching the chosen location's
 * carrier.
 */
export const useBulkAssignLocation = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      taskIds: string[];
      carrierDropoffLocationId: string;
    }) => {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/v2/tasks/bulk-assign-location`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
};
